import React, { useState } from 'react';
import { Box, Typography, Paper, Grid, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Button, Alert, Link } from '@mui/material';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { CorporateActionTypeLabels, CorporateActionTypeColors } from '../../stockTradingEnums';

// ── Helpers ──────────────────────────────────────────────────────────────────
const formatPrice = (val) => val != null ? `$${Number(val).toFixed(2)}` : '-';
const formatPercent = (val) => val != null ? `${Number(val).toFixed(2)}%` : '-';
const formatDate = (val) => val ? new Date(val).toLocaleDateString() : '-';
const formatNumber = (val) => val != null ? Number(val).toLocaleString() : '-';

const formatLargeNumber = (val) => {
    if (val == null) return '-';
    if (Math.abs(val) >= 1e12) return `${(val / 1e12).toFixed(2)}T`;
    if (Math.abs(val) >= 1e9) return `${(val / 1e9).toFixed(2)}B`;
    if (Math.abs(val) >= 1e6) return `${(val / 1e6).toFixed(2)}M`;
    if (Math.abs(val) >= 1e3) return `${(val / 1e3).toFixed(2)}K`;
    return val.toFixed(2);
};

// ── Shared sub-components ────────────────────────────────────────────────────
const SectionTitle = ({ children }) => (
    <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 600, fontSize: '1rem' }}>{children}</Typography>
);
const EmptyState = ({ message }) => (
    <Box sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary">{message || 'No data available'}</Typography></Box>
);
const TabLoading = () => (
    <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
);

const COLORS = { green: '#22c55e', red: '#ef4444', orange: '#f59e0b', blue: '#3b82f6', purple: '#8b5cf6', cyan: '#06b6d4', gray: '#6b7280' };

// ── Key-fact card ────────────────────────────────────────────────────────────
const FactCard = ({ label, value, href }) => (
    <Grid item xs={6} sm={3}>
        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', height: '100%' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{label}</Typography>
            {href ? (
                <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontWeight: 600, fontSize: '0.95rem', wordBreak: 'break-all' }}>
                    {value}
                </Link>
            ) : (
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>{value}</Typography>
            )}
        </Paper>
    </Grid>
);

// ── Valuation row ────────────────────────────────────────────────────────────
const ValuationRow = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
    </Box>
);

// ── Waterfall bar ────────────────────────────────────────────────────────────
const WaterfallItem = ({ label, value, maxVal, color }) => {
    const pct = maxVal > 0 ? Math.max((Math.abs(value) / maxVal) * 100, 2) : 0;
    return (
        <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>{formatLargeNumber(value)}</Typography>
            </Box>
            <Box sx={{ width: '100%', bgcolor: 'action.hover', borderRadius: 1, height: 12 }}>
                <Box sx={{ width: `${pct}%`, bgcolor: color, borderRadius: 1, height: '100%' }} />
            </Box>
        </Box>
    );
};

// ── Custom tooltip for Recharts ──────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <Paper sx={{ p: 1.5, boxShadow: 3 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>{label}</Typography>
            {payload.map((entry, i) => (
                <Typography key={i} variant="caption" sx={{ display: 'block', color: entry.color }}>
                    {entry.name}: {formatLargeNumber(entry.value)}
                </Typography>
            ))}
        </Paper>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// Main Component
// ═════════════════════════════════════════════════════════════════════════════
const FinancialTab = ({ data, loading, error, symbolId, symbolData }) => {
    const [financialPeriod, setFinancialPeriod] = useState('quarterly'); // 'annual' | 'quarterly'

    if (loading) return <TabLoading />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!data) return <TabLoading />;

    const f = data.fundamentals || {};
    const earnings = data.earnings || [];
    const corporateActions = data.corporate_actions || [];

    const financialsData = financialPeriod === 'annual' ? (f.annual_financials || []) : (f.quarterly_financials || []);
    const latestAnnual = (f.annual_financials || [])[0];

    // Ownership pie data
    const insiderPct = (f.held_percent_insiders || 0) * 100;
    const institutionPct = (f.held_percent_institutions || 0) * 100;
    const freeFloatPct = Math.max(100 - insiderPct - institutionPct, 0);
    const closelyHeldPct = insiderPct + institutionPct;
    const ownershipData = [
        { name: 'Free Float', value: parseFloat(freeFloatPct.toFixed(2)) },
        { name: 'Closely Held', value: parseFloat(closelyHeldPct.toFixed(2)) },
    ];
    const PIE_COLORS = [COLORS.blue, COLORS.orange];

    // Capital structure bar data
    const capitalData = [
        { name: 'Structure', marketCap: f.market_cap || 0, debt: f.total_debt || 0, cash: f.total_cash || 0, enterpriseValue: f.enterprise_value || 0 },
    ];

    // Waterfall data from latest annual
    const waterfallItems = latestAnnual ? [
        { label: 'Revenue', value: latestAnnual.total_revenue, color: COLORS.blue },
        { label: 'Cost of Revenue', value: -(latestAnnual.cost_of_revenue || 0), color: COLORS.red },
        { label: 'Gross Profit', value: latestAnnual.gross_profit, color: COLORS.green },
        { label: 'Operating Expenses', value: latestAnnual.gross_profit && latestAnnual.operating_income ? -(latestAnnual.gross_profit - latestAnnual.operating_income) : 0, color: COLORS.orange },
        { label: 'Operating Income', value: latestAnnual.operating_income, color: COLORS.cyan },
        { label: 'Net Income', value: latestAnnual.net_income, color: latestAnnual.net_income >= 0 ? COLORS.green : COLORS.red },
    ] : [];
    const waterfallMax = waterfallItems.length > 0 ? Math.max(...waterfallItems.map(w => Math.abs(w.value || 0)), 1) : 1;

    // Performance chart data (sorted oldest to newest)
    const perfData = [...financialsData].reverse().map(q => ({
        period: q.period,
        Revenue: q.total_revenue,
        'Net Income': q.net_income,
    }));

    return (
        <Box>
            {/* ── Section 1: Key Facts ────────────────────────────────── */}
            <SectionTitle>Key Facts</SectionTitle>
            <Grid container spacing={2}>
                <FactCard label="Market Capitalization" value={formatLargeNumber(f.market_cap)} />
                <FactCard label="Dividend Yield" value={f.dividend_yield != null ? formatPercent(f.dividend_yield * 100) : '-'} />
                <FactCard label="P/E Ratio (TTM)" value={f.pe_ratio != null ? Number(f.pe_ratio).toFixed(2) : '-'} />
                <FactCard label="Basic EPS (TTM)" value={f.eps_ttm != null ? `$${Number(f.eps_ttm).toFixed(2)}` : '-'} />
                <FactCard label="Founded" value={f.founded_year || '-'} />
                <FactCard label="Employees" value={f.employees != null ? formatNumber(f.employees) : '-'} />
                <FactCard label="CEO" value={f.ceo_name || '-'} />
                <FactCard label="Website" value={f.website ? new URL(f.website).hostname : '-'} href={f.website || undefined} />
            </Grid>

            {/* About */}
            {f.about && (
                <Paper variant="outlined" sx={{ p: 2, mt: 2, mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>About</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{f.about}</Typography>
                </Paper>
            )}

            {/* ── Section 2: Ownership + Capital Structure ────────────── */}
            <SectionTitle>Ownership & Capital Structure</SectionTitle>
            <Grid container spacing={2}>
                {/* LEFT: Ownership donut */}
                <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Ownership</Typography>
                        {f.held_percent_insiders == null && f.held_percent_institutions == null ? (
                            <EmptyState message="No ownership data" />
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <ResponsiveContainer width="100%" height={220}>
                                    <PieChart>
                                        <Pie data={ownershipData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3}>
                                            {ownershipData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                                        </Pie>
                                        <Tooltip formatter={(v) => `${v.toFixed(2)}%`} />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>
                        )}
                    </Paper>
                </Grid>

                {/* RIGHT: Capital Structure */}
                <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Capital Structure</Typography>
                        {!f.market_cap && !f.total_debt && !f.total_cash && !f.enterprise_value ? (
                            <EmptyState message="No capital structure data" />
                        ) : (
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={capitalData} layout="vertical" margin={{ left: 10, right: 30, top: 10, bottom: 10 }}>
                                    <XAxis type="number" tickFormatter={formatLargeNumber} />
                                    <YAxis type="category" dataKey="name" hide />
                                    <Tooltip content={<ChartTooltip />} />
                                    <Legend />
                                    <Bar dataKey="marketCap" name="Market Cap" stackId="a" fill={COLORS.blue} />
                                    <Bar dataKey="debt" name="Debt" stackId="a" fill={COLORS.red} />
                                    <Bar dataKey="cash" name="Cash" stackId="a" fill={COLORS.green} />
                                    <Bar dataKey="enterpriseValue" name="Enterprise Value" fill={COLORS.purple} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* ── Section 3: Valuation ────────────────────────────────── */}
            <SectionTitle>Valuation</SectionTitle>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <ValuationRow label="P/E Ratio (TTM)" value={f.pe_ratio != null ? Number(f.pe_ratio).toFixed(2) : '-'} />
                <ValuationRow label="Forward P/E" value={f.forward_pe != null ? Number(f.forward_pe).toFixed(2) : '-'} />
                <ValuationRow label="Price / Sales" value={f.price_to_sales != null ? Number(f.price_to_sales).toFixed(2) : '-'} />
                <ValuationRow label="Price / Book" value={f.price_to_book != null ? Number(f.price_to_book).toFixed(2) : '-'} />
                <ValuationRow label="Beta" value={f.beta != null ? Number(f.beta).toFixed(2) : '-'} />
            </Paper>

            {/* ── Section 4: Growth & Profitability ────────────────────── */}
            <SectionTitle>Growth & Profitability</SectionTitle>

            {/* Performance chart */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Performance</Typography>
                    <Box>
                        <Button size="small" variant={financialPeriod === 'annual' ? 'contained' : 'outlined'} onClick={() => setFinancialPeriod('annual')} sx={{ mr: 1, textTransform: 'none', minWidth: 70 }}>Annual</Button>
                        <Button size="small" variant={financialPeriod === 'quarterly' ? 'contained' : 'outlined'} onClick={() => setFinancialPeriod('quarterly')} sx={{ textTransform: 'none', minWidth: 80 }}>Quarterly</Button>
                    </Box>
                </Box>
                {perfData.length === 0 ? <EmptyState message="No financial data available" /> : (
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={perfData} margin={{ left: 10, right: 10, top: 5, bottom: 5 }}>
                            <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                            <YAxis tickFormatter={formatLargeNumber} tick={{ fontSize: 11 }} />
                            <Tooltip content={<ChartTooltip />} />
                            <Legend />
                            <Bar dataKey="Revenue" fill={COLORS.blue} radius={[3, 3, 0, 0]} />
                            <Bar dataKey="Net Income" fill={COLORS.green} radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </Paper>

            {/* Revenue to Profit Conversion waterfall */}
            {latestAnnual && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                        Revenue to Profit Conversion {latestAnnual.period ? `(${latestAnnual.period})` : ''}
                    </Typography>
                    {waterfallItems.map((item, i) => (
                        <WaterfallItem key={i} label={item.label} value={item.value} maxVal={waterfallMax} color={item.color} />
                    ))}
                </Paper>
            )}

            {/* ── Section 5: Estimates ─────────────────────────────────── */}
            <SectionTitle>Estimates</SectionTitle>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                {f.analyst_target_mean == null && f.recommendation_key == null ? (
                    <EmptyState message="No analyst estimates available" />
                ) : (
                    <>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="caption" color="text.secondary">Low</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.red }}>{formatPrice(f.analyst_target_low)}</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="caption" color="text.secondary">Mean Target</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.blue }}>{formatPrice(f.analyst_target_mean)}</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="caption" color="text.secondary">High</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.green }}>{formatPrice(f.analyst_target_high)}</Typography>
                                </Box>
                            </Grid>
                        </Grid>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                            {f.recommendation_key && (
                                <Chip
                                    label={`Recommendation: ${f.recommendation_key.toUpperCase()}`}
                                    size="small"
                                    color={['buy', 'strong_buy'].includes(f.recommendation_key) ? 'success' : ['sell', 'strong_sell'].includes(f.recommendation_key) ? 'error' : 'default'}
                                />
                            )}
                            {f.number_of_analysts != null && (
                                <Chip label={`${f.number_of_analysts} Analyst${f.number_of_analysts !== 1 ? 's' : ''}`} size="small" variant="outlined" />
                            )}
                        </Box>
                    </>
                )}
            </Paper>

            {/* ── Existing: Earnings table ─────────────────────────────── */}
            <SectionTitle>Earnings</SectionTitle>
            {earnings.length === 0 ? <EmptyState message="No earnings data" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Quarter</TableCell>
                                <TableCell align="right">Est. EPS</TableCell>
                                <TableCell align="right">Actual EPS</TableCell>
                                <TableCell align="right">Surprise %</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {earnings.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>{formatDate(row.earnings_date)}</TableCell>
                                    <TableCell>{row.fiscal_quarter || '-'}</TableCell>
                                    <TableCell align="right">{row.estimated_eps != null ? `$${Number(row.estimated_eps).toFixed(2)}` : '-'}</TableCell>
                                    <TableCell align="right">{row.actual_eps != null ? `$${Number(row.actual_eps).toFixed(2)}` : '-'}</TableCell>
                                    <TableCell align="right">
                                        {row.surprise_percent != null ? (
                                            <Typography
                                                component="span"
                                                sx={{ color: row.surprise_percent >= 0 ? COLORS.green : COLORS.red, fontWeight: 500 }}
                                            >
                                                {row.surprise_percent >= 0 ? '+' : ''}{Number(row.surprise_percent).toFixed(2)}%
                                            </Typography>
                                        ) : '-'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* ── Existing: Corporate Actions table ────────────────────── */}
            <SectionTitle>Corporate Actions</SectionTitle>
            {corporateActions.length === 0 ? <EmptyState message="No corporate actions" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Type</TableCell>
                                <TableCell>Ex-Date</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell>Description</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {corporateActions.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>
                                        <Chip label={CorporateActionTypeLabels[row.action_type] || row.action_type} size="small" color={CorporateActionTypeColors[row.action_type] || 'default'} />
                                    </TableCell>
                                    <TableCell>{formatDate(row.ex_date)}</TableCell>
                                    <TableCell align="right">{row.amount != null ? `$${Number(row.amount).toFixed(4)}` : '-'}</TableCell>
                                    <TableCell>{row.description || '-'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default FinancialTab;
